import { useState, useEffect } from "react";
import { Users, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import {
  getInactiveMiningUsersAPI,
  getMiningUserDetailsAPI,
  updateMiningUserStatusAPI,
} from "../../api/miningApi";
import { MiningUserDetailsModal } from "../../components/MiningUserDetailsModal";

export default function MiningInactiveUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const pageSize = 10;

  useEffect(() => {
    fetchUsers(currentPage);
  }, [currentPage]);

  const fetchUsers = async (page) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getInactiveMiningUsersAPI(page, pageSize);
      if (response.status) {
        setUsers(response.data.data);
        setTotalUsers(response.data.total_rows);
        setTotalPages(Math.ceil(response.data.total_rows / pageSize));
      } else {
        setError("Failed to fetch users");
      }
    } catch (err) {
      setError("Failed to fetch users");
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
    setDetailsLoading(true);
    setUserDetails(null);
    try {
      const response = await getMiningUserDetailsAPI(user.userId);
      if (response.success) {
        setUserDetails({
          ...response.data,
          appOpens: user.appOpens || response.data.appOpens || [],
          miningStops: user.miningStops || response.data.miningStops || [],
        });
      } else {
        console.error("Failed to fetch user details");
      }
    } catch (err) {
      console.error("Error fetching user details:", err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
    setUserDetails(null);
  };

  const handleStatusToggle = async (userId, newStatus) => {
    try {
      const response = await updateMiningUserStatusAPI({ userId, status: newStatus });
      if (response.success) {
        setUsers(users.map((user) =>
          user.userId === userId ? { ...user, accountStatus: newStatus } : user
        ));
        if (userDetails && userDetails.userId === userId) {
          setUserDetails({ ...userDetails, accountStatus: newStatus });
        }
      } else {
        console.error("Failed to update user status");
      }
    } catch (err) {
      console.error("Error updating user status:", err);
    }
  };

  const renderPagination = () => {
    return Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
      if (
        page === 1 ||
        page === totalPages ||
        (page >= currentPage - 1 && page <= currentPage + 1)
      ) {
        return (
          <button
            key={page}
            onClick={() => setCurrentPage(page)}
            className={`h-8 w-8 rounded text-xs font-medium transition-colors ${
              page === currentPage
                ? "bg-ginox-green/20 border border-ginox-green/60 text-ginox-green"
                : "border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
            }`}
          >
            {page}
          </button>
        );
      }
      if (page === currentPage - 2 || page === currentPage + 2) {
        return (
          <span key={page} className="h-8 w-8 flex items-center justify-center text-muted-foreground text-xs">
            ...
          </span>
        );
      }
      return null;
    });
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-400 text-base mb-4">{error}</p>
          <button
            onClick={() => fetchUsers(currentPage)}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
          <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
          Mining
        </div>
        <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
          Inactive Users <span className="text-gradient-ginox">Management</span>
        </h1>
        <p className="text-sm text-muted-foreground">Manage and monitor inactive user accounts</p>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              {["Name", "User ID", "Status", "Mining Count", "Created At", "Username", "Actions"].map((h) => (
                <th key={h} className="border-b border-border/70 px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="py-12 text-center text-sm text-muted-foreground">Loading...</td>
              </tr>
            ) : users.length > 0 ? (
              users.map((user) => (
                <tr key={user._id} className="group hover:bg-white/[0.02] transition-colors">
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{user.name}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{user.userId}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm">
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20">
                      {user.accountStatus}
                    </span>
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{user.miningCount}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{user.username}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm">
                    <button
                      onClick={() => handleViewDetails(user)}
                      className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors flex items-center gap-1.5"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      More Info
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="py-12 text-center text-sm text-muted-foreground">No inactive users found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {(totalPages > 1 || loading) && (
        <div className="flex flex-col items-center gap-3 md:flex-row md:items-center md:justify-between">
          <p className="text-xs text-muted-foreground">
            {loading
              ? "Loading..."
              : `Showing ${(currentPage - 1) * pageSize + 1} to ${Math.min(currentPage * pageSize, totalUsers)} of ${totalUsers} users`}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1 || loading}
              className="h-8 w-8 rounded border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            {!loading && renderPagination()}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || loading}
              className="h-8 w-8 rounded border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      <MiningUserDetailsModal
        userDetails={userDetails}
        isOpen={isModalOpen}
        onClose={closeModal}
        onStatusToggle={handleStatusToggle}
        loading={detailsLoading}
      />
    </div>
  );
}
